import { EventEmitter } from '@angular/core';
import { LONG_MONTH_NAMES, SHORT_MONTH_NAMES } from 'Core/redesign/utils/date';
import * as i0 from "@angular/core";
/* eslint-disable prefer-destructuring */
/**
 * HoldingsDatePickerDirective
 *
 * This directive allows the user to select a MM/YY date. (Only month and year, no specific day, can be selected).
 * Only a specified set of dates will be shown for the user to select. This is useful
 * in cases where we want to restrict selection to a small of valid date options. This component also allows
 * the user to navigate to the next and previous valid date options with arrow buttons
 *
 * Input Properties:
 *   dateOptions: list of Date() options for the date picker to display for selection
 *
 * Output Properties:
 *   dateChangeHandler: Emits the newly selected date when a new date is selected. If there are multiple dates with
 *                      the same MM/YYYY combination, the earliest of t
 */
export class HoldingsDatePickerDirective {
    constructor() {
        this.dateChangeHandler = new EventEmitter();
        this.disableNext = false;
        this.disablePrev = false;
        // Dropdown Configs
        this.monthDropdownConfig = {
            title: { label: '' },
            list: [],
        };
        this.yearDropdownConfig = {
            title: { label: '' },
            list: [],
        };
    }
    ngOnInit() {
        // Convert string date options to JS Date objects.  Sort Ascending: Earlier dates first
        this.sortedDates = [...this.dateOptions].sort((d1, d2) => Number(d1) - Number(d2));
        this.earliestDate = this.sortedDates[0];
        this.latestDate = this.sortedDates[this.sortedDates.length - 1];
        // Get mapping to help determine which dates to display
        this.yearsToMonthsMapping = this.getYearToMonthsMapping();
        const sortedStringYears = Object.keys(this.yearsToMonthsMapping).sort();
        this.sortedYears = sortedStringYears.map((year) => Number(year));
        // The latest date should be initially selected
        const maxYear = Number(this.sortedYears[this.sortedYears.length - 1]);
        this.selectedYear = maxYear;
        this.selectedMonth = Math.max(...this.yearsToMonthsMapping[maxYear]);
        // Set Dropdown Configs
        this.setMonthDropdownConfig();
        this.setYearDropdownConfig();
        this.checkArrowButtons();
    }
    getYearToMonthsMapping() {
        const mapping = {};
        this.sortedDates.forEach((date) => {
            const year = date.getFullYear();
            const month = date.getMonth();
            if (!(year in mapping)) {
                mapping[year] = [];
            }
            if (!mapping[year].includes(month)) {
                mapping[year].push(month);
            }
        });
        return mapping;
    }
    setMonthDropdownConfig() {
        const monthOptionDropdownList = this.yearsToMonthsMapping[this.selectedYear].map((month) => {
            return {
                label: LONG_MONTH_NAMES[month],
                selectedLabel: SHORT_MONTH_NAMES[month],
                value: month,
                selected: month === this.selectedMonth,
            };
        });
        this.monthDropdownConfig = {
            title: { label: '' },
            list: monthOptionDropdownList,
        };
    }
    setYearDropdownConfig() {
        const yearOptionDropdownList = this.sortedYears.map((year) => {
            return {
                label: String(year),
                value: year,
                selected: year === this.selectedYear,
            };
        });
        this.yearDropdownConfig = {
            title: { label: '' },
            list: yearOptionDropdownList,
        };
    }
    checkArrowButtons() {
        // checks if either of the prev/next arrow buttons should be disabled
        this.disableNext =
            this.selectedMonth === this.latestDate.getMonth() && this.selectedYear === this.latestDate.getFullYear();
        this.disablePrev =
            this.selectedMonth === this.earliestDate.getMonth() &&
                this.selectedYear === this.earliestDate.getFullYear();
    }
    handleDateChange() {
        // Match the selected MM/YYYY with the corresponding date option
        const selectedDate = this.sortedDates.find((date) => {
            return this.selectedYear === date.getFullYear() && this.selectedMonth === date.getMonth();
        });
        this.dateChangeHandler.emit(selectedDate);
        this.checkArrowButtons();
    }
    selectMonthHandler(month) {
        this.selectedMonth = month;
        this.handleDateChange();
    }
    selectYearHandler(year) {
        this.selectedYear = year;
        // If we select a year for which the current selected month is not valid, auto-select the nearest valid month
        // for the new year. If there is a tie, choose the later month
        const validMonthsForNewYear = [...this.yearsToMonthsMapping[this.selectedYear]];
        const currentMonth = this.selectedMonth;
        if (!validMonthsForNewYear.includes(currentMonth)) {
            validMonthsForNewYear.sort((m1, m2) => {
                const m1Diff = Math.abs(this.selectedMonth - m1);
                const m2Diff = Math.abs(this.selectedMonth - m2);
                return m1Diff - m2Diff === 0 ? m1 - m2 : m1Diff - m2Diff;
            });
            this.selectedMonth = validMonthsForNewYear[0];
        }
        // Only show months that are valid for the newly selected year in the dropdown
        this.setMonthDropdownConfig();
        this.handleDateChange();
    }
    nextDate() {
        const validMonthsForCurrentYear = this.yearsToMonthsMapping[this.selectedYear];
        const indexOfCurrentMonth = validMonthsForCurrentYear.indexOf(this.selectedMonth);
        const indexOfCurrentYear = this.sortedYears.indexOf(this.selectedYear);
        const currentMonthIsLastMonth = indexOfCurrentMonth === validMonthsForCurrentYear.length - 1;
        const currentYearIsLastYear = indexOfCurrentYear === this.sortedYears.length - 1;
        if (!currentMonthIsLastMonth) {
            this.selectedMonth = validMonthsForCurrentYear[indexOfCurrentMonth + 1];
        }
        if (currentMonthIsLastMonth && !currentYearIsLastYear) {
            const nextYear = this.sortedYears[indexOfCurrentYear + 1];
            const validMonthsForNextYear = this.yearsToMonthsMapping[nextYear];
            this.selectedYear = nextYear;
            this.selectedMonth = validMonthsForNextYear[0];
            this.setMonthDropdownConfig();
        }
        this.handleDateChange();
    }
    prevDate() {
        const validMonthsForCurrentYear = this.yearsToMonthsMapping[this.selectedYear];
        const indexOfCurrentMonth = validMonthsForCurrentYear.indexOf(this.selectedMonth);
        const indexOfCurrentYear = this.sortedYears.indexOf(this.selectedYear);
        const currentMonthIsFirstMonth = indexOfCurrentMonth === 0;
        const currentYearIsFirstYear = indexOfCurrentYear === 0;
        if (!currentMonthIsFirstMonth) {
            this.selectedMonth = validMonthsForCurrentYear[indexOfCurrentMonth - 1];
        }
        if (currentMonthIsFirstMonth && !currentYearIsFirstYear) {
            const prevYear = this.sortedYears[indexOfCurrentYear - 1];
            const validMonthsForPrevYear = this.yearsToMonthsMapping[prevYear];
            this.selectedYear = prevYear;
            this.selectedMonth = validMonthsForPrevYear[validMonthsForPrevYear.length - 1];
            this.setMonthDropdownConfig();
        }
        this.handleDateChange();
    }
}
HoldingsDatePickerDirective.ɵfac = function HoldingsDatePickerDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || HoldingsDatePickerDirective)(); };
HoldingsDatePickerDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: HoldingsDatePickerDirective, inputs: { dateOptions: "dateOptions" }, outputs: { dateChangeHandler: "dateChangeHandler" } });
